import clsx from "clsx";
import React, { CSSProperties, ReactNode, useContext } from "react";
import { _LandingSectionBaseRoot } from "./LandingSectionBase.styled";
import { ImageResponsive, ImageUrlContext } from "../../Image";
import { ImageFit, ImageFormat } from "../../Image/interface/ImageData";
import { isEmptyImageId } from "../../Image/Image.utils";
import { IImageResponsiveProps } from "../../Image/ImageResponsive";
import LandingVideo from "../LandingVideo/LandingVideo";
import LandingAnimatedImage from "./LandingAnimatedImage/LandingAnimatedImage";
import useBreakpoint from "../../Grid/hooks/useBreakpoint";
import StyledDiv from "../../StyledDiv/StyledDiv";

interface CustomCSSProperties extends CSSProperties {
  "--aspect-ratio-mobile"?: string | number;
}

export type TAnimationVariant = "st-valentin";

export interface ILandingSectionBaseProps {
  image?: string;
  video?: string;
  animation?: TAnimationVariant;
  renderImage?: React.ReactNode | (() => React.ReactNode);
  backgroundImage?: {
    desktop?: string;
    mobile?: string;
  };
  imageLeft?: boolean;
  topContent?: React.ReactNode;
  topContentSize?: "default" | "small";
  bottomContent?: React.ReactNode;
  styleTopContainer?: React.CSSProperties;
  topVariant?: "center" | "align-left";
  topPosition?: "top" | "column";
  onImageClick?(): void;
  boxContainer?: boolean;
  imageAspectRatio?: number;
  className?: string;
  bottomClassName?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  mobileMinHeight?: string;
  /** Couche absolue derrière le contenu (ex. vidéo de fond). */
  backgroundBehindContainer?: ReactNode;
}

const LandingSectionBase = (props: ILandingSectionBaseProps) => {
  const {
    image,
    backgroundImage,
    imageLeft = false,
    topContent = null,
    bottomContent = null,
    styleTopContainer,
    topVariant = "center",
    topPosition = "column",
    video,
    onImageClick,
    topContentSize = "default",
    animation,
    boxContainer,
    imageAspectRatio,
    className,
    renderImage,
    bottomClassName,
    style,
    children,
    mobileMinHeight,
    backgroundBehindContainer,
  } = props;
  const { generateImageUrl } = useContext(ImageUrlContext);
  const { mobile } = useBreakpoint();
  const isTopPosition = topPosition === "top";
  const desktopAspectRatio = imageAspectRatio || (isTopPosition ? 3 / 2 : 1);
  const mobileAspectRatio = imageAspectRatio || 1;

  const imageProps: IImageResponsiveProps = isTopPosition
    ? {
        alt: "",
        desktop: {
          image: {
            id: image,
            fit: ImageFit.COVER,
            quality: 80,
            aspectRatio: desktopAspectRatio,
            width: 904,
          },
          imgStyle: {
            borderRadius: 4,
            overflow: "hidden",
            aspectRatio: desktopAspectRatio,
            maxWidth: 904,
          },
        },
        mobile: {
          image: {
            id: image,
            fit: ImageFit.COVER,
            quality: 100,
            aspectRatio: mobileAspectRatio,
            outputPixelDensities: [1, 2],
            width: 640,
          },
          imgStyle: {
            borderRadius: 4,
            overflow: "hidden",
            aspectRatio: mobileAspectRatio,
            maxWidth: 640,
          },
        },
      }
    : {
        alt: "",
        desktop: {
          image: {
            id: image,
            fit: ImageFit.COVER,
            quality: 80,
            aspectRatio: desktopAspectRatio,
            width: 904,
          },
          imgStyle: {
            borderRadius: 4,
            overflow: "hidden",
            aspectRatio: desktopAspectRatio,
            maxWidth: 904,
          },
        },
        mobile: {
          image: {
            id: image,
            fit: ImageFit.COVER,
            quality: 100,
            aspectRatio: mobileAspectRatio,
            outputPixelDensities: [1, 2],
            width: 640,
          },
          imgStyle: {
            borderRadius: 4,
            overflow: "hidden",
            aspectRatio: mobileAspectRatio,
            maxWidth: 640,
          },
        },
      };

  return (
    <_LandingSectionBaseRoot
      $topPosition={isTopPosition}
      $topContentSize={topContentSize}
      $hasTopContent={!!topContent}
      className={clsx(
        "landing-section-base landing-section-base__head js-active",
        {
          "image-left": imageLeft,
          "align-left": topVariant === "align-left",
        },
        className
      )}
      $imageLeft={imageLeft}
      $bg={{
        desktop:
          (backgroundImage &&
            backgroundImage.desktop &&
            !isEmptyImageId(backgroundImage.desktop) &&
            generateImageUrl({
              id: backgroundImage?.desktop || "-1",
              width: "auto",
              height: "auto",
              quality: 100,
              format: ImageFormat.WEBP,
            })) ||
          undefined,
        mobile:
          (backgroundImage &&
            backgroundImage.mobile &&
            !isEmptyImageId(backgroundImage.mobile) &&
            generateImageUrl({
              id: backgroundImage?.mobile || "-1",
              width: typeof window !== "undefined" ? window.innerWidth : "auto",
              height: "auto",
              quality: 90,
              format: ImageFormat.WEBP,
            })) ||
          undefined,
      }}
      style={style}
      $mobileMinHeight={mobileMinHeight}
    >
      {backgroundBehindContainer ? (
        <div className="landing-section-base__bg-behind">
          {backgroundBehindContainer}
        </div>
      ) : null}
      <div
        className={clsx("landing-section-base__head__container", {
          "has-children": !!children,
        })}
        style={
          boxContainer
            ? {
                maxWidth: 1440,
                margin: "0 auto",
              }
            : undefined
        }
      >
        {children ? (
          <StyledDiv
            styles={{
              gridColumn: 1 / -1,
              // gridRow: 1 / -1,
            }}
          >
            {children}
          </StyledDiv>
        ) : (
          <>
            {topContent && (
              <div
                className={clsx("landing-section-base__head__top", {
                  "align-left": topVariant === "align-left",
                })}
              >
                <div
                  className={clsx(
                    "landing-section-base__head__top__container",
                    {
                      "align-left": topVariant === "align-left",
                    }
                  )}
                  style={styleTopContainer}
                >
                  {topContent}
                </div>
              </div>
            )}
            <div
              className="landing-section-base__head__middle"
              style={
                {
                  "--aspect-ratio-mobile": mobileAspectRatio,
                } as CustomCSSProperties
              }
            >
              <div
                className="landing-section-base__head__middle__container"
                style={{ overflow: video ? "hidden" : undefined }}
              >
                {renderImage ? (
                  typeof renderImage === "function" ? (
                    renderImage()
                  ) : (
                    renderImage
                  )
                ) : animation ? (
                  <LandingAnimatedImage
                    variant={animation}
                    style={
                      (isTopPosition &&
                        !mobile && {
                          maxWidth: 600,
                          width: "70%",
                          marginLeft: imageLeft ? "auto" : undefined,
                        }) ||
                      undefined
                    }
                  />
                ) : video ? (
                  <LandingVideo
                    videoId={video}
                    poster={{
                      desktop: image || "-1",
                      mobile: image || "-1",
                    }}
                    aspectRatio={desktopAspectRatio}
                  />
                ) : (
                  image && (
                    <span
                      className="landing-section-base__head__img"
                      onClick={onImageClick}
                    >
                      <ImageResponsive {...imageProps} />
                    </span>
                  )
                )}
              </div>
            </div>
            <div
              className={clsx(
                "landing-section-base__head__bottom",
                {
                  "align-left": topVariant === "align-left",
                  "fill-top-content": !topContent,
                },
                bottomClassName
              )}
              style={
                (isTopPosition &&
                  !mobile && {
                    margin: !imageLeft ? 0 : undefined,
                    marginLeft: !imageLeft ? "auto" : undefined,
                  }) ||
                undefined
              }
            >
              <div className="landing-section-base__head__bottom__container">
                <div className="landing-section-base__head__content">
                  {bottomContent}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </_LandingSectionBaseRoot>
  );
};

export default LandingSectionBase;
